import React, { useEffect, useState } from "react";
import { clearSession } from "../shared/_helpers/session";
import { Link, useParams, useNavigate } from "react-router-dom";
import Loader from "../components/Loader";
import { checkBlockedStatus, getOnboardingDetails, onboardingQuestions } from "../shared/_services/common.services";
import { setQuickSetup } from "../shared/_services/auth.services";

const Q7_OPEN_ENDED_PROMPTS = [
    "Have you worked with a financial advisor before?",
    "When you\u2019re making financial decisions, what do you usually rely on?",
    "Tell us about your life (family, job, hobbies).",
    "Anything important (non-financial) going on? (Stress, big life changes, caregiving, career shifts, etc.)",
    "What would you like to talk about first with your advisor?",
];

function renderQ7OpenEndedAnswers(allQuestions, optionAmounts) {
    const questionIndex = allQuestions.findIndex(q => q.questionNo === 8);
    const amounts = questionIndex !== -1 ? optionAmounts[questionIndex] : null;
    const lastIndex = Q7_OPEN_ENDED_PROMPTS.length - 1;

    return (
        <ul className="mb-0 pl-3">
            {Q7_OPEN_ENDED_PROMPTS.map((prompt, index) => (
                <li key={prompt} className={index < lastIndex ? "mb-2" : undefined}>
                    <b>{prompt}</b>
                    <div className="text-answer">{amounts?.[prompt] || "N/A"}</div>
                </li>
            ))}
        </ul>
    );
}

function isClientUser(userRole) {
    return userRole === "client";
}

function shouldShowExtendedSections(userRole, quickSetup) {
    return !(isClientUser(userRole) && quickSetup === true);
}

function getQuestionIndexByNo(allQuestions, questionNo) {
    return allQuestions.findIndex((q) => q.questionNo === questionNo);
}

function getQuestionBundleByNo(allQuestions, selectedOptions, othersList, questionNo) {
    const questionIndex = getQuestionIndexByNo(allQuestions, questionNo);
    return {
        selectedData: questionIndex !== -1 ? selectedOptions[questionIndex] : null,
        others: questionIndex !== -1 ? othersList[questionIndex] : null,
    };
}

function renderSimpleOptionsAnswer(allQuestions, selectedOptions, othersList, questionNo, filterInvalid = false) {
    const { selectedData, others } = getQuestionBundleByNo(allQuestions, selectedOptions, othersList, questionNo);
    const safeOptions = Array.isArray(selectedData) ? selectedData : [];
    const displayOptions = filterInvalid
        ? safeOptions.filter((option) => option !== null && option !== undefined)
        : safeOptions;

    if (displayOptions.length === 0) {
        return <span>N/A</span>;
    }

    return (
        <ul className="mb-0 pl-3">
            {displayOptions.map((option) => (
                <li key={option}>{option}</li>
            ))}
            {others && others.trim() !== "" && <li>{others}</li>}
        </ul>
    );
}

function renderQuestionFourAnswer(allQuestions, selectedOptions, othersList) {
    const { selectedData, others } = getQuestionBundleByNo(allQuestions, selectedOptions, othersList, 5);
    if (!selectedData?.length) {
        return <span>N/A</span>;
    }

    return (
        <ul className="mb-0 pl-3">
            {selectedData.map((option) => (
                <li key={option}>{option}</li>
            ))}
            {others && others.trim() !== "" && (
                <li>
                    <b>What are you planning to buy, and about how much?</b>
                    <br />- {others}
                </li>
            )}
        </ul>
    );
}

function renderAmountList(questionAmounts) {
    return (
        <ul className="mb-0 pl-3">
            {Object.entries(questionAmounts).map(([option, value]) => (
                <li key={option}>
                    <div className="d-flex">
                        <span className="text-astra w-25 f-600 mr-4">{option}</span>
                        {value && value.trim() !== "" ? `$${parseInt(value, 10).toLocaleString()}` : ""}
                    </div>
                </li>
            ))}
        </ul>
    );
}

function renderDebtsAnswer(allQuestions, optionAmounts) {
    const questionIndex = getQuestionIndexByNo(allQuestions, 6);
    const questionAmounts = questionIndex !== -1 ? optionAmounts[questionIndex] : null;
    return questionAmounts && Object.entries(questionAmounts).length > 0
        ? renderAmountList(questionAmounts)
        : <p>N/A</p>;
}

function renderInvestmentsAnswer(allQuestions, selectedOptions, optionAmounts) {
    const questionIndex = getQuestionIndexByNo(allQuestions, 7);
    const questionData = questionIndex !== -1 ? selectedOptions[questionIndex] : null;
    const questionAmounts = questionIndex !== -1 ? optionAmounts[questionIndex] : null;

    if (!questionData?.length) {
        return <p>N/A</p>;
    }
    return renderAmountList(questionAmounts || {});
}

function renderPlanningColumn(allQuestions, selectedOptions, othersList, questionNo, questionName, heading) {
    const question = allQuestions.find((q) => q.questionNo === questionNo && q.questionName === questionName);
    const questionIndex = question ? allQuestions.findIndex((q) => q._id === question._id) : -1;
    const questionData = questionIndex !== -1 ? selectedOptions[questionIndex] : null;
    const questionOthers = questionIndex !== -1 ? othersList[questionIndex] : null;

    return (
        <>
            <div className="mb-2"><b>{question?.question_name}</b></div>
            <h6 className="f-600 text-astra"><span style={{ fontSize: "10px" }}>▶</span> {heading}</h6>
            {questionData?.length ? (
                <ul className="mb-0 pl-3">
                    {questionData.map((option) => (
                        <li key={option}>{option}</li>
                    ))}
                    {questionOthers && questionOthers.trim() !== "" && (
                        <li>{questionOthers}</li>
                    )}
                </ul>
            ) : (
                <span>N/A</span>
            )}
        </>
    );
}

function renderQuestionThreeAnswer(allQuestions, selectedOptions, othersList) {
    return (
        <div className="d-flex gap-25">
            <div>
                {renderPlanningColumn(allQuestions, selectedOptions, othersList, 3, "Short-term", "Short Term Planning")}
            </div>
            <div>
                {renderPlanningColumn(allQuestions, selectedOptions, othersList, 4, "Long-term", "Long Term Planning")}
            </div>
        </div>
    );
}

function QuestionnaireAccordion({
    index,
    title,
    editTo,
    userRole,
    openAccordions,
    toggleAccordion,
    children,
}) {
    return (
        <div className={`single-question-dtl qa-accordian-item ${openAccordions[index] ? "open" : "closed"}`}>
            <div className="question-text qa-accordian-header" onClick={() => toggleAccordion(index)}>
                <div className="d-flex justify-content-between align-items-center">
                    <h5>{title}</h5>
                    <div className="d-flex">
                        {isClientUser(userRole) && (
                            <Link to={editTo} state={{ fromUserQuestionnaire: true }}>
                                <i className="iconoir-edit-pencil mr-2"></i>
                            </Link>
                        )}
                        <i className={`iconoir-nav-arrow-down qa-accordian-icon ${openAccordions[index] ? "rotated" : ""}`}></i>
                    </div>
                </div>
            </div>
            <div className={`answer-text qa-accordian-body ${openAccordions[index] ? "show" : "hide"}`}>
                {children}
            </div>
        </div>
    );
}

const UserQuestionnaireDtls = () => {
    const [selectedOptions, setSelectedOptions] = useState([]);
    const params = useParams();
    
    
    
    const [optionAmounts, setOptionAmounts] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [allQuestions, setAllQuestions] = useState([]);
    const userId = params?.clientId;
    const navigate = useNavigate();
    const [openAccordions, setOpenAccordions] = useState({});
    const userRole = JSON.parse(localStorage.getItem("currentUser"))?.user?.userType;
    const quickSetup = JSON.parse(localStorage.getItem("currentUser"))?.user?.quickSetup;
    const isClient = isClientUser(userRole);
    const showExtendedSections = shouldShowExtendedSections(userRole, quickSetup);
    const [othersList, setOthersList] = useState([]);



    const checkAndFillData = async () => {
        setIsLoading(true);

        try {
        // Fetch all questions
        const questionsResponse = await onboardingQuestions();

        if (questionsResponse.status === 200) {
            // #69: same crash class as the onboarding screens — a malformed
            // 200 body isn't necessarily undefined (an object body is still
            // truthy), so `?? []` alone doesn't catch it, and every render
            // helper below calls .findIndex/.find on allQuestions unguarded.
            // `questions?.map` alone isn't enough either — optional chaining
            // makes the map call return undefined rather than throw, but
            // Promise.all(undefined) still throws, which would have skipped
            // setIsLoading(false) and left the screen stuck loading.
            const questions = Array.isArray(questionsResponse.data) ? questionsResponse.data : [];
            setAllQuestions(questions);
            const onboardingDetailsPromises = questions.map(question =>
                getOnboardingDetails(question._id, userId || JSON.parse(window.localStorage.getItem("currentUser"))?.user?._id || "", "questionaire")
            );
            const onboardingDetailsResponses = await Promise.all(onboardingDetailsPromises);

            const updatedSelectedOptions = [];
            const updatedOptionAmounts = [];
            const updatedOthersList = [];

            onboardingDetailsResponses.forEach((response, index) => {
                if (response.status === 200 && response?.data?.data?.length > 0) {
                    const { selectedOptions: selectedData, others } = response?.data?.data[0];

                    const tempSelectedOptions = [];
                    const tempOptionAmounts = {};

                    selectedData.forEach(option => {
                        tempSelectedOptions.push(option.name);
                        tempOptionAmounts[option.name] = option.value ?? "";

                        
                    });

                    updatedSelectedOptions.push(tempSelectedOptions);
                    updatedOptionAmounts.push(tempOptionAmounts);
                    updatedOthersList.push(others);
                } else {
                    updatedSelectedOptions.push([]);
                    updatedOptionAmounts.push({});
                    updatedOthersList.push("");
                }
            });
            setSelectedOptions(updatedSelectedOptions);
            setOptionAmounts(updatedOptionAmounts);
            setOthersList(updatedOthersList)
        }
        } finally {
            // #69: whatever happens above, the screen must not get stuck
            // loading — a finally here catches any exception, not only the
            // one this fix already closed.
            setIsLoading(false);
        }
    };

    const checkBlocked = async () => {
        const userId = JSON.parse(localStorage.getItem('currentUser')).user?._id;
        try {
            // Call the API to check the user's blocked status
            const response = await checkBlockedStatus(userId);

            if (response.data ? response.status == 401 : response.response.status == 401) {
                clearSession();
                navigate("/login");
            }
            else {
                const { isBlocked, isDeleted } = response.data;

                if (isBlocked || isDeleted) {
                    // Clear the currentUser from localStorage
                    clearSession();

                    // Navigate to the login page
                    navigate("/login");
                }
            }
        } catch (error) {
            console.error("Error checking blocked status:", error);
            // Optionally handle errors, e.g., show an error message
        }
    }

    useEffect(() => {
        checkBlocked();
        checkAndFillData();
    }, []);


    const toggleAccordion = (questionIndex) => {
        setOpenAccordions(prev => ({
            ...prev,
            [questionIndex]: !prev[questionIndex]
        }));
    };

    const handleFullSetupQuestionnaire = async (e) => {
        e.preventDefault();
        try {
            const currentUserStr = localStorage.getItem("currentUser");
            if (currentUserStr) {
                const currentUser = JSON.parse(currentUserStr);
                const userId = currentUser?.user?._id;
                if (userId) {
                    const response = await setQuickSetup(userId, false);
                    if (response?.status === 200 || response?.status === 201) {
                        // Update localStorage after successful API call
                        if (currentUser && currentUser.user) {
                            const updatedUser = {
                                ...currentUser,
                                user: {
                                    ...currentUser.user,
                                    quickSetup: false
                                }
                            };
                            localStorage.setItem("currentUser", JSON.stringify(updatedUser));
                        }
                    }
                }
            }
        } catch (error) {
            console.error("Error in handleFullSetupQuestionnaire:", error);
        }
        navigate("/q1");
    };

    return (
        <div>
            {isLoading ? (
                <Loader />
            ) : (
                <div className="px-5 py-5">
                    <div className="user-question-dtls-box mx-5">
                        <div className="user-question-dtls-box-header">
                            <h5>Q&A Library</h5>
                            {isClient && quickSetup === false && (
                                <Link to="/q1" className="as-btn-primary profile-light-btn px-3 py-2">
                                    <i className="fa fa-repeat mr-2" aria-hidden="true"></i>
                                    Re-take Questionnaire
                                </Link>
                            )}
                            {isClient && quickSetup === true && (
                                <Link
                                    to="/q1"
                                    className="as-btn-primary profile-light-btn px-3 py-2"
                                    onClick={handleFullSetupQuestionnaire}
                                >
                                    <i className="fa fa-repeat mr-2" aria-hidden="true"></i>
                                    Full Setup Questionnaire
                                </Link>
                            )}
                        </div>
                        {/* Question 1  */}
                        <QuestionnaireAccordion
                            index={0}
                            title="What do you want help with first?"
                            editTo="/q1"
                            userRole={userRole}
                            openAccordions={openAccordions}
                            toggleAccordion={toggleAccordion}
                        >
                            {renderSimpleOptionsAnswer(allQuestions, selectedOptions, othersList, 1, true)}
                        </QuestionnaireAccordion>

                        {/* Question 2  */}
                        {showExtendedSections && (
                        <QuestionnaireAccordion
                            index={1}
                            title="Which areas do you want clarity or help with?"
                            editTo="/q2"
                            userRole={userRole}
                            openAccordions={openAccordions}
                            toggleAccordion={toggleAccordion}
                        >
                            {renderSimpleOptionsAnswer(allQuestions, selectedOptions, othersList, 2)}
                        </QuestionnaireAccordion>
                        )}

                        {/* Question 3 */}
                        {showExtendedSections && (
                        <QuestionnaireAccordion
                            index={2}
                            title=" What kind of planning support would be helpful?"
                            editTo="/q3"
                            userRole={userRole}
                            openAccordions={openAccordions}
                            toggleAccordion={toggleAccordion}
                        >
                            {renderQuestionThreeAnswer(allQuestions, selectedOptions, othersList)}
                        </QuestionnaireAccordion>
                        )}

                        {/* Question 4 */}
                        {showExtendedSections && (
                        <QuestionnaireAccordion
                            index={3}
                            title="Anything coming up that might affect your money or taxes?"
                            editTo="/q4"
                            userRole={userRole}
                            openAccordions={openAccordions}
                            toggleAccordion={toggleAccordion}
                        >
                            {renderQuestionFourAnswer(allQuestions, selectedOptions, othersList)}
                        </QuestionnaireAccordion>
                        )}

                        {/* Question 5  */}
                        {showExtendedSections && (
                        <QuestionnaireAccordion
                            index={4}
                            title="Debts"
                            editTo="/q5"
                            userRole={userRole}
                            openAccordions={openAccordions}
                            toggleAccordion={toggleAccordion}
                        >
                            {renderDebtsAnswer(allQuestions, optionAmounts)}
                        </QuestionnaireAccordion>
                        )}

                        {/* Question 6  */}
                        {showExtendedSections && (
                        <QuestionnaireAccordion
                            index={5}
                            title="Investments"
                            editTo="/q6"
                            userRole={userRole}
                            openAccordions={openAccordions}
                            toggleAccordion={toggleAccordion}
                        >
                            {renderInvestmentsAnswer(allQuestions, selectedOptions, optionAmounts)}
                        </QuestionnaireAccordion>
                        )}

                        {/* Question 7  */}
                        <QuestionnaireAccordion
                            index={6}
                            title="Let’s help your advisor understand your world."
                            editTo="/q7"
                            userRole={userRole}
                            openAccordions={openAccordions}
                            toggleAccordion={toggleAccordion}
                        >
                            {renderQ7OpenEndedAnswers(allQuestions, optionAmounts)}
                        </QuestionnaireAccordion>


                    </div>
                </div>

            )}
        </div>
    );
};

export default UserQuestionnaireDtls;
